import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Table, Modal, Form, Alert, Spinner, Tab, Nav } from 'react-bootstrap';
import { FaArrowLeft, FaEdit, FaEye, FaTrash, FaPlus, FaCalculator, FaCar, FaUser, FaFileAlt, FaExclamationTriangle, FaShieldAlt, FaMoneyBillWave, FaClipboardList } from 'react-icons/fa';
import {
    getVehicleById,
    updateVehicleStatus,
    addExpense,
    deleteDocument,
    getCurrentUser,
    handleApiError
} from '../services/api';
import api from '../services/api';

// import business rules
import {
    canVehicleBeSold,
    canVehicleBeArchived,
    getEnhancedStatusBadge,
    getStatusLabel,
    isDocumentCriticalForStatus
} from '../utils/vehicleBusinessRules';

// ✅ IMPORT FORM COMPONENTS FOR DISPLAY
import TrafficInsuranceForm from '../components/forms/TrafficInsuranceForm';
import CarExpertizMap from '../components/CarExpertizMap';
import OwnershipHistoryForm from '../components/forms/OwnershipHistoryForm'; 



const VehicleDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // ✅ TAB STATE - UPDATED TO MATCH AddVehiclePage
    const [activeTab, setActiveTab] = useState('documents');

    const [vehicle, setVehicle] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentUser, setCurrentUser] = useState(null);

    // ✅ Vehicle documents state
    const [vehicleDocuments, setVehicleDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);

    // Modal states
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [expenseForm, setExpenseForm] = useState({
        type: 'repair',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
    });

    const [showSaleModal, setShowSaleModal] = useState(false);
    const [saleForm, setSaleForm] = useState({
        type: 'individual',
        name: '',
        contact: '',
        date: new Date().toISOString().split('T')[0]
    });

    // ✅ TAB NAVIGATION CONFIG - UPDATED TO MATCH AddVehiclePage
    const tabs = [
        { key: 'documents', title: 'Belgeler', icon: FaClipboardList, color: 'purple' },
        { key: 'basic', title: 'Temel Bilgiler', icon: FaCar, color: 'primary' },
        { key: 'price', title: 'Fiyat', icon: FaMoneyBillWave, color: 'success' },
        { key: 'insurance', title: 'Trafik Sigortası', icon: FaShieldAlt, color: 'info' },  
        { key: 'expenses', title: 'Masraflar', icon: FaCalculator, color: 'danger' },
        { key: 'history', title: 'Alım-Satım Geçmişi', icon: FaUser, color: 'warning' }
    ];

    // ✅ HELPER FUNCTION - Display value or "Bilgi Yok"
    const displayValue = (value, defaultText = 'Bilgi Yok', formatter = null) => {
        if (value === null || value === undefined || value === '') {
            return (
                <span className="text-danger fst-italic" style={{ fontSize: '0.9em' }}>
                    {defaultText}
                </span>
            );
        }

        if (formatter && typeof formatter === 'function') {
            return formatter(value);
        }

        return <span className="text-muted" style={{ fontSize: '0.9em' }}>{value}</span>;
    };

    // ✅ NO-OP CHANGE HANDLER FOR TrafficInsuranceForm DISPLAY
    const noOpChangeHandler = () => {
        // Display mode - no changes allowed
    };

    useEffect(() => {
        const user = getCurrentUser();
        setCurrentUser(user);
        fetchVehicleDetail();
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchVehicleDocuments();
        }
    }, [id]);

    const fetchVehicleDetail = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await getVehicleById(id + '?cache_bust=' + Date.now());

            if (response.success) {
        
                setVehicle(response.vehicle);
                setAnalytics(response.analytics);
                setCanEdit(response.canEdit);

            } else {
                setError('Araç detayı yüklenirken hata oluştu');
            }
        } catch (error) {
            setError(handleApiError(error));
        } finally {
            setLoading(false);
        }
    };

    const fetchVehicleDocuments = async () => {
        try {
            setDocumentsLoading(true);
            console.log(`📄 Fetching documents for vehicle: ${id}`);

            const response = await api.get(`/documents/vehicle/${id}`);

            if (response.data.success) {
                setVehicleDocuments(response.data.documents);
                console.log(`✅ Found ${response.data.documents.length} documents`);
            } else {
                console.log('❌ No documents found for vehicle');
                setVehicleDocuments([]);
            }
        } catch (error) {
            console.error('❌ Documents fetch error:', error);
            setVehicleDocuments([]);
        } finally {
            setDocumentsLoading(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {

            let statusData = { status: newStatus };

            if (newStatus === 'sold') {
                statusData.soldTo = saleForm;
            }

            const result = await updateVehicleStatus(id, statusData);

            if (result.success) {
                console.log('✅ Status update success:', result);
                fetchVehicleDetail();
                setShowSaleModal(false);

                if (window.toast) {
                    window.toast.success(`Araç durumu ${newStatus} olarak güncellendi`);
                }
            }
        } catch (error) {
            console.error('❌ Status change error:', error);
            handleApiError(error);
        }
    };

    const handleAddExpense = async (e) => {
        e.preventDefault();

        try {
            const result = await addExpense(id, expenseForm);

            if (result.success) {
                fetchVehicleDetail();
                setShowExpenseModal(false);
                setExpenseForm({
                    type: 'repair',
                    amount: '',
                    description: '',
                    date: new Date().toISOString().split('T')[0]
                });

                if (window.toast) {
                    window.toast.success('Masraf başarıyla eklendi');
                }
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleDeleteDocument = async (documentId) => {
        if (!window.confirm('Bu belgeyi silmek istediğinizden emin misiniz?')) {
            return;
        }

        try {
            console.log(`🗑️ Deleting document: ${documentId}`);
            const response = await api.delete(`/documents/${documentId}`);

            if (response.data.success) {
                fetchVehicleDocuments();

                if (window.toast) {
                    window.toast.success('Belge başarıyla silindi');
                }
            }
        } catch (error) {
            console.error('❌ Document delete error:', error);
            handleApiError(error);
        }
    };

    const handleDownloadDocument = async (document) => {
        try {
            const response = await fetch(`/api/documents/${document._id}/download`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = window.document.createElement('a');  // ✅ window.document kullan
            a.href = url;
            a.download = document.file?.originalName || document.originalName || 'document.pdf';
            window.document.body.appendChild(a);  // ✅ window.document kullan
            a.click();
            window.document.body.removeChild(a);  // ✅ window.document kullan
                window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
        }
    };

    const handleViewDocument = (document) => {
        let originalUrl = document.file?.cloudinaryUrl || document.url;
        const mimeType = document.file?.mimeType || '';

        if (mimeType === 'application/pdf') {
            // PDF için Google Docs Viewer kullan
            let previewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(originalUrl)}&embedded=true`;
            window.open(previewUrl, '_blank');
        } else {
            // JPG/PNG için direkt aç
            window.open(originalUrl, '_blank');
        }
    };

    // Helper functions
    const masrafTypes = {
        bakim_onarim: 'Bakım & Onarım',
        yakit: 'Yakıt',
        temizlik_detay: 'Temizlik & Detay',
        hasar_onarim: 'Hasar Onarım',
        muayene: 'Muayene',
        vergi_harc: 'Vergi & Harç',
        park_otopark: 'Park & Otopark',
        diger: 'Diğer',
        // Legacy support
        repair: 'Onarım',
        maintenance: 'Bakım',
        cleaning: 'Temizlik',
        transport: 'Nakliye',
        documents: 'Evrak',
        insurance: 'Sigorta',
        tax: 'Vergi',
        other: 'Diğer'
    };

    const documentTypes = {
        'ruhsat': 'Ruhsat',
        'alis_sozlesmesi': 'Alış Sözleşmesi',
        'satis_sozlesmesi': 'Satış Sözleşmesi',
        'trafik_sigortasi': 'Trafik Sigortası',
        'sigorta_iptal_policesi': 'Sigorta İptal Poliçesi',
        'nufus_cuzdani': 'Nüfus Cüzdanı',
        'ehliyet': 'Ehliyet',
        'gelir_belgesi': 'Gelir Belgesi',
        'alis_faturasi': 'Alış Faturası',
        'satis_faturasi': 'Satış Faturası',
        'ekspertiz_raporu': 'Ekspertiz Raporu'
    };

    const formatNumber = (num) => {
        if (!num) return '0';
        return num.toLocaleString('tr-TR');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleDateString('tr-TR');
    };

    // Loading state
    if (loading) {
        return (
            <Container className="py-5">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Yükleniyor...</span>
                    </Spinner>
                    <p className="mt-3">Araç detayı yükleniyor...</p>
                </div>
            </Container>
        );
    }

    // Error state
    if (error || !vehicle) {
        return (
            <Container className="py-5">
                <Alert variant="danger" className="text-center">
                    <h4>⚠️ Araç Bulunamadı</h4>
                    <p>{error || 'Aradığınız araç bulunamadı.'}</p>
                    <Button as={Link} to="/vehicles" variant="primary">
                        <FaArrowLeft className="me-2" />
                        Araç Listesine Dön
                    </Button>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="py-5">
            {/* ✅ ENHANCED HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center">
                    <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/vehicles')}
                        className="me-3"
                    >
                        <FaArrowLeft />
                    </Button>
                    <div>
                        <h1 className="mb-1">
                            <FaCar className="me-2 text-primary" />
                            {vehicle.brand && vehicle.model && vehicle.year
                                ? `${vehicle.brand} ${vehicle.model} ${vehicle.year}`
                                : displayValue(vehicle.title, 'Araç bilgisi eksik')
                            }
                        </h1>
                        <p className="text-muted mb-0">
                            Plaka: {displayValue(vehicle.plateNumber, 'Plaka bilgisi yok')}
                        </p>
                    </div>
                </div>
                <div>
                    {getEnhancedStatusBadge(vehicle, vehicleDocuments)}
                </div>
            </div>

            {/* ✅ Business Rules Uyarı Kartı */}
            {(vehicle.status === 'sold' && !canVehicleBeSold(vehicleDocuments)) ||
                (vehicle.status === 'archived' && !canVehicleBeArchived(vehicle, vehicleDocuments)) ? (
                <Alert variant="danger" className="mb-4">
                    <h6><FaExclamationTriangle className="me-2" />Kritik Uyarı!</h6>
                    {vehicle.status === 'sold' && !canVehicleBeSold(vehicleDocuments) && (
                        <p className="mb-1">🚨 Araç satıldı olarak işaretli ama <strong>noter onaylı satış sözleşmesi</strong> eksik!</p>
                    )}
                    {vehicle.status === 'archived' && !canVehicleBeArchived(vehicle, vehicleDocuments) && (
                        <p className="mb-0">🚨 Araç arşivlendi ama <strong>sigorta iptal poliçesi</strong> veya <strong>iade miktarı</strong> eksik!</p>
                    )}
                </Alert>
            ) : null}

            <Row>
                <Col lg={8}>
                    {/* ✅ TAB NAVIGATION */}
                    <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                        <Card className="mb-4">
                            <Card.Header className="p-0">
                                <Nav variant="tabs" className="border-0">
                                    {tabs.map(tab => {
                                        const IconComponent = tab.icon;
                                        return (
                                            <Nav.Item key={tab.key}>
                                                <Nav.Link
                                                    eventKey={tab.key}
                                                    className={`d-flex align-items-center px-3 py-3 ${activeTab === tab.key ? 'active' : ''}`}
                                                >
                                                    <IconComponent
                                                        className={`me-2 text-${tab.color}`}
                                                        size={16}
                                                    />
                                                    <span className="fw-bold">{tab.title}</span>
                                                </Nav.Link>
                                            </Nav.Item>
                                        );
                                    })}
                                </Nav>
                            </Card.Header>

                            <Card.Body className="p-4">
                                <Tab.Content>
                                    {/* ✅ BASIC TAB - DISPLAY STRUCTURE */}
                                    <Tab.Pane eventKey="basic">
                                        <Card className="mb-4">
                                            <Card.Header>
                                                <h5 className="mb-0">🚗 Araç Temel Bilgileri</h5>
                                            </Card.Header>
                                            <Card.Body>
                                                {/* Yasal Zorunlu Bilgiler */}
                                                <div className="mb-4">
                                                    <h6 className="mb-3">📋 Yasal Zorunlu Bilgiler</h6>
                                                    <Row>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Plaka Numarası</Form.Label>
                                                                <div>{displayValue(vehicle.plateNumber)}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Şase Numarası</Form.Label>
                                                                <div style={{ fontFamily: 'monospace' }}>{displayValue(vehicle.chassisNumber)}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">İlan Başlığı</Form.Label>
                                                                <div>{displayValue(vehicle.title)}</div>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </div>

                                                {/* Marka & Model */}
                                                <div className="border-top pt-3 mb-4">
                                                    <h6 className="mb-3">🏷️ Marka & Model</h6>
                                                    <Row>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Marka</Form.Label>
                                                                <div>{displayValue(vehicle.brand)}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Model</Form.Label>
                                                                <div>{displayValue(vehicle.model)}</div>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </div>

                                                {/* Teknik Bilgiler */}
                                                <div className="border-top pt-3 mb-4">
                                                    <h6 className="mb-3">⚙️ Teknik Bilgiler</h6>
                                                    <Row>
                                                        <Col md={3}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Kasa Tipi</Form.Label>
                                                                <div>{displayValue(vehicle.bodyType)}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Model Yılı</Form.Label>
                                                                <div>{displayValue(vehicle.year)}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Yakıt Tipi</Form.Label>
                                                                <div className="text-capitalize">{displayValue(vehicle.fuelType)}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Vites Tipi</Form.Label>
                                                                <div className="text-capitalize">{displayValue(vehicle.transmission)}</div>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>

                                                    <Row>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Kilometre</Form.Label>
                                                                <div>{displayValue(vehicle.mileage, 'Kilometre bilgisi yok', (val) => `${formatNumber(val)} km`)}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Renk</Form.Label>
                                                                <div>{displayValue(vehicle.color)}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Temsilci</Form.Label>
                                                                <div>{displayValue(vehicle.addedBy?.name, 'Temsilci bilgisi yok')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>

                                                    {/* Açıklama */}
                                                    <Row>
                                                        <Col md={12}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Ek Özellikler</Form.Label>
                                                                <div>{displayValue(vehicle.description, 'Ek özellik eklenmemiş')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>

                                                    {/* ✅ YENİ: Gelişmiş Teknik Bilgiler */}
                                                    <div className="border-top pt-3 mb-4">
                                                        <h6 className="mb-3">🔧 Gelişmiş Teknik Bilgiler</h6>
                                                        <Row>
                                                            <Col md={3}>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="text-muted">Silindir Hacmi</Form.Label>
                                                                    <div>{displayValue(vehicle.technicalDetails?.engineDisplacement, 'Silindir hacmi bilgisi yok', (val) => `${val} cc`)}</div>
                                                                </Form.Group>
                                                            </Col>
                                                            <Col md={3}>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="text-muted">Motor Gücü</Form.Label>
                                                                    <div>{displayValue(vehicle.technicalDetails?.horsepower, 'Motor gücü bilgisi yok', (val) => `${val} kW`)}</div>
                                                                </Form.Group>
                                                            </Col>
                                                            <Col md={3}>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="text-muted">Garanti Durumu</Form.Label>
                                                                    <div>
                                                                        {vehicle.technicalDetails?.warrantyStatus === 'Var' ? (
                                                                            <Badge bg="success">Garanti Var</Badge>
                                                                        ) : vehicle.technicalDetails?.warrantyStatus === 'Yok' ? (
                                                                            <Badge bg="danger">Garanti Yok</Badge>
                                                                        ) : vehicle.technicalDetails?.warrantyCustom ? (
                                                                            <Badge bg="info">{vehicle.technicalDetails.warrantyCustom}</Badge>
                                                                        ) : (
                                                                            displayValue(null, 'Garanti bilgisi yok')
                                                                        )}
                                                                    </div>
                                                                </Form.Group>
                                                            </Col>
                                                            <Col md={3}>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="text-muted">Muayene Tarihi</Form.Label>
                                                                    <div>{displayValue(formatDate(vehicle.technicalDetails?.inspectionDate), 'Muayene tarihi yok')}</div>
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>
                                                    </div>

                                                    {/* ✅ YENİ: Etiket Bilgileri */}
                                                    <div className="border-top pt-3 mb-4">
                                                        <h6 className="mb-3">🏷️ Etiket Bilgileri</h6>
                                                        <Row>
                                                            <Col md={12}>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="text-muted">Sahibinden URL</Form.Label>
                                                                    <div>
                                                                        {vehicle.technicalDetails?.sahibindenUrl ? (
                                                                            <a href={vehicle.technicalDetails.sahibindenUrl} target="_blank" rel="noopener noreferrer" className="text-break">
                                                                                {vehicle.technicalDetails.sahibindenUrl}
                                                                            </a>
                                                                        ) : (
                                                                            displayValue(null, 'Sahibinden URL yok')
                                                                        )}
                                                                    </div>
                                                                </Form.Group>
                                                            </Col>
                                                            
                                                        </Row>
                                                    </div>

                                                    {/* Tramer Bilgileri */}
                                                    <div className="border-top pt-3 mb-4">
                                                        <h6 className="mb-3">🚨 Tramer Bilgileri</h6>
                                                        <Row>
                                                            <Col md={3}>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="text-muted">Tramer Durumu</Form.Label>
                                                                    <div>
                                                                        <Badge bg={vehicle.hasTramer ? 'danger' : 'success'}>
                                                                            {vehicle.hasTramer ? 'Tramer Var' : 'Tramer Yok'}
                                                                        </Badge>
                                                                    </div>
                                                                </Form.Group>
                                                            </Col>
                                                            {vehicle.hasTramer && (
                                                                <>
                                                                    <Col md={3}>
                                                                        <Form.Group className="mb-3">
                                                                            <Form.Label className="text-muted">Tramer Miktarı</Form.Label>
                                                                            <div>{displayValue(vehicle.tramerAmount, 'Tutar bilgisi yok', (val) => `${formatNumber(val)} ₺`)}</div>
                                                                        </Form.Group>
                                                                    </Col>
                                                                    <Col md={6}>
                                                                        <Form.Group className="mb-3">
                                                                            <Form.Label className="text-muted">Tramer Açıklaması</Form.Label>
                                                                            <div>{displayValue(vehicle.tramerDescription, 'Açıklama yok')}</div>
                                                                        </Form.Group>
                                                                    </Col>
                                                                </>
                                                            )}
                                                        </Row>
                                                    </div>

                                                    {/* Finansman Bilgileri */}
                                                    <div className="border-top pt-3 mb-4">
                                                        <h6 className="mb-3">💰 Finansman</h6>
                                                        <Row>
                                                            <Col md={6}>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="text-muted">Kasko Kodu</Form.Label>
                                                                    <div>{displayValue(vehicle.kaskoKodu, 'Kasko kodu yok')}</div>
                                                                </Form.Group>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Form.Group className="mb-3">
                                                                    <Form.Label className="text-muted">Kasko Bedeli</Form.Label>
                                                                    <div>{displayValue(vehicle.kaskoBedeli, 'Kasko bedeli yok', (val) => `${new Intl.NumberFormat('tr-TR').format(val)} ₺`)}</div>
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>
                                                    </div>

                                                {/* ✅ YENİ: Expertiz Bilgileri */}
                                                <div className="border-top pt-3">
                                                    <h6 className="mb-3">🔍 Expertiz Durumu</h6>

                                                    {/* Expertiz Map - Display Mode */}
                                                    <div className="mb-3">
                                                        <CarExpertizMap
                                                            initialExpertizData={vehicle.expertizData || {}}
                                                            onChange={() => { }} // No-op for display mode
                                                            disabled={true}
                                                            showLegend={true}
                                                        />
                                                    </div>

                                                    {/* Expertiz Notları */}
                                                    <Row>
                                                        <Col md={12}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Expertiz Notları</Form.Label>
                                                                <div>{displayValue(vehicle.expertizNotes, 'Expertiz notu eklenmemiş')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Tab.Pane>

                                    {/* ✅ PRICE TAB - DISPLAY STRUCTURE */}
                                    <Tab.Pane eventKey="price">
                                        <Card className="mb-4">
                                            <Card.Header>
                                                <h5 className="mb-0">💰 Fiyat Bilgileri & Alım-Satım</h5>
                                            </Card.Header>
                                            <Card.Body>
                                                {/* Fiyat ve Tarih Bilgileri */}
                                                <Row className="mb-4">
                                                    <Col md={3}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="text-muted">Alış Fiyatı</Form.Label>
                                                            <div>{displayValue(vehicle.purchasePrice, 'Alış fiyatı yok', (val) => `${formatNumber(val)} ₺`)}</div>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="text-muted">Satış Fiyatı</Form.Label>
                                                            <div>{displayValue(vehicle.salePrice, 'Satış fiyatı yok', (val) => `${formatNumber(val)} ₺`)}</div>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="text-muted">Alış Tarihi</Form.Label>
                                                            <div >{displayValue(formatDate(vehicle.purchaseDate), 'Alış tarihi yok')}</div>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="text-muted">Satış Tarihi</Form.Label>
                                                            <div >{displayValue(formatDate(vehicle.saleDate), 'Satış tarihi yok')}</div>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="text-muted">📄 Fatura Numarası</Form.Label>
                                                            <div>{displayValue(vehicle.invoiceNumber, 'Fatura numarası yok')}</div>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>

                                                {/* Satıcı Bilgileri */}
                                                <div className="border-top pt-3 mb-4">
                                                    <h6 className="mb-3">👤 Satıcı Bilgileri (Kimden Alındı)</h6>
                                                    <Row>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Satıcı Adı</Form.Label>
                                                                <div>{displayValue(vehicle.purchasedFrom?.name, 'Satıcı adı yok')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">T.C. / Vergi No</Form.Label>
                                                                <div style={{ fontFamily: 'monospace' }}>{displayValue(vehicle.purchasedFrom?.idNumber, 'T.C./Vergi no yok')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Telefon</Form.Label>
                                                                <div>{displayValue(vehicle.purchasedFrom?.phone, 'Telefon bilgisi yok')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">E-posta</Form.Label>
                                                                <div>{displayValue(vehicle.purchasedFrom?.email, 'E-posta bilgisi yok')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Adres</Form.Label>
                                                                <div>{displayValue(vehicle.purchasedFrom?.address, 'Adres bilgisi yok')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </div>

                                                {/* Alıcı Bilgileri */}
                                                <div className="border-top pt-3">
                                                    <h6 className="mb-3">🤝 Alıcı Bilgileri (Kime Satıldı)</h6>
                                                    <Row>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Alıcı Adı</Form.Label>
                                                                <div>{displayValue(vehicle.soldTo?.name, 'Alıcı adı yok')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">T.C. / Vergi No</Form.Label>
                                                                <div style={{ fontFamily: 'monospace' }}>{displayValue(vehicle.soldTo?.idNumber, 'T.C./Vergi no yok')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Telefon</Form.Label>
                                                                <div>{displayValue(vehicle.soldTo?.phone, 'Telefon bilgisi yok')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">E-posta</Form.Label>
                                                                <div>{displayValue(vehicle.soldTo?.email, 'E-posta bilgisi yok')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-muted">Adres</Form.Label>
                                                                <div>{displayValue(vehicle.soldTo?.address, 'Adres bilgisi yok')}</div>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Tab.Pane>

                                    {/* ✅ INSURANCE TAB - USING TrafficInsuranceForm */}
                                    <Tab.Pane eventKey="insurance">
                                        <TrafficInsuranceForm
                                            data={vehicle}
                                            handleChange={noOpChangeHandler}
                                            mode="display"
                                            vehicleStatus={vehicle.status}
                                            disabled={true}
                                        />
                                    </Tab.Pane>

                                    {/* ✅ DOCUMENTS TAB */}
                                    <Tab.Pane eventKey="documents">
                                        {documentsLoading ? (
                                            <div className="text-center py-3">
                                                <Spinner animation="border" size="sm" />
                                                <p className="mt-2 text-muted">Belgeler yükleniyor...</p>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Belgeler Listesi */}
                                                {vehicleDocuments && vehicleDocuments.length > 0 ? (
                                                    <Row>
                                                        {vehicleDocuments.map((document, index) => (
                                                            <Col md={6} key={document._id || index} className="mb-3">
                                                                <Card border="light">
                                                                    <Card.Body>
                                                                        <div className="d-flex justify-content-between align-items-start">
                                                                            <div className="flex-grow-1">
                                                                                <h6 className="fw-bold mb-1">
                                                                                    {documentTypes[document.category] || document.category}
                                                                                    {/* Kritik belge işareti */}
                                                                                    {((vehicle.status === 'sold' &&
                                                                                        document.category === 'satis_sozlesmesi' &&
                                                                                        (document.title?.toLowerCase().includes('noter') ||
                                                                                            document.notes?.toLowerCase().includes('noter'))) ||
                                                                                        (vehicle.status === 'archived' &&
                                                                                            (document.category === 'sigorta_iptal_policesi' ||
                                                                                                (document.category === 'trafik_sigortasi' &&
                                                                                                    document.notes?.toLowerCase().includes('iptal'))))) && (
                                                                                            <Badge bg="success" size="sm" className="ms-2">KRITIK</Badge>
                                                                                        )}
                                                                                </h6>
                                                                                <p className="text-muted small mb-1">
                                                                                    {document.file?.originalName || document.title}
                                                                                </p>
                                                                                <p className="text-muted small mb-1">
                                                                                    Boyut: {document.fileSizeMB ? `${document.fileSizeMB} MB` : 'Bilinmiyor'}
                                                                                </p>
                                                                                <p className="text-muted small mb-0">
                                                                                    {new Date(document.createdAt).toLocaleDateString('tr-TR')}
                                                                                </p>
                                                                                {document.notes && (
                                                                                    <p className="text-muted small mt-1 fst-italic">
                                                                                        "{document.notes}"
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                            <div className="d-flex flex-column gap-1 ms-2">
                                                                                <Button
                                                                                    variant="outline-primary"
                                                                                    size="sm"
                                                                                    onClick={() => handleViewDocument(document)}
                                                                                    type="button"
                                                                                >
                                                                                    <FaEye />
                                                                                </Button>
                                                                                {canEdit && (
                                                                                    <Button
                                                                                        variant="outline-danger"
                                                                                        size="sm"
                                                                                        onClick={() => handleDeleteDocument(document._id)}
                                                                                    >
                                                                                        <FaTrash />
                                                                                    </Button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </Card.Body>
                                                                </Card>
                                                            </Col>
                                                        ))}
                                                    </Row>
                                                ) : (
                                                    <Alert variant="info" className="text-center">
                                                        <FaFileAlt className="me-2" />
                                                        Henüz belge eklenmemiş
                                                        <br />
                                                        <small className="text-muted">
                                                            Belgeler AddVehiclePage veya document upload sistemi ile eklenebilir
                                                        </small>
                                                    </Alert>
                                                )}
                                            </>
                                        )}
                                    </Tab.Pane>

                                    {/* ✅ NEW EXPENSES TAB */}
                                    <Tab.Pane eventKey="expenses">
                                        <Card>
                                            <Card.Header className="d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">💰 Masraflar</h6>
                                                {canEdit && (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => setShowExpenseModal(true)}
                                                    >
                                                        <FaPlus className="me-1" />
                                                        Masraf Ekle
                                                    </Button>
                                                )}
                                            </Card.Header>
                                            <Card.Body>
                                                {vehicle.expenses && vehicle.expenses.length > 0 ? (
                                                    <Table striped hover responsive>
                                                        <thead>
                                                            <tr>
                                                                <th>Tip</th>
                                                                <th>Tutar</th>
                                                                <th>Açıklama</th>
                                                                <th>Tarih</th>
                                                                <th>Satıcı</th>
                                                                <th>Ekleyen</th>

                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {vehicle.expenses.map((expense, index) => (
                                                                <tr key={index}>
                                                                    <td>
                                                                        <Badge bg="secondary">
                                                                            {masrafTypes[expense.type] || expense.type}
                                                                        </Badge>
                                                                    </td>
                                                                    <td className="fw-bold text-danger">
                                                                        {formatNumber(expense.amount)} ₺
                                                                    </td>
                                                                    <td>{displayValue(expense.description, 'Açıklama yok')}</td>
                                                                    <td>{displayValue(formatDate(expense.date), 'Tarih yok')}</td>
                                                                    <td>{displayValue(expense.vendor, 'Satıcı bilgisi yok')}</td>
                                                                    <td style={{ fontSize: '0.8em' }}>
                                                                        {expense.addedBy?.name || 'Bilinmiyor'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot>
                                                            <tr className="table-light">
                                                                <td><strong>TOPLAM</strong></td>
                                                                <td><strong className="text-danger">
                                                                    {formatNumber(vehicle.expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0))} ₺
                                                                </strong></td>
                                                                <td colSpan="3"></td>
                                                            </tr>
                                                        </tfoot>
                                                    </Table>
                                                ) : (
                                                    <Alert variant="info" className="text-center">
                                                        Henüz masraf eklenmemiş
                                                    </Alert>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Tab.Pane>

                                    {/* ✅ YENİ: OWNERSHIP HISTORY TAB */}
                                    <Tab.Pane eventKey="history">
                                        <OwnershipHistoryForm
                                            vehicle={vehicle}
                                            mode="display"
                                        />
                                    </Tab.Pane>
                                </Tab.Content>
                            </Card.Body>
                        </Card>
                    </Tab.Container>
                </Col>

                <Col lg={4}>
                    {/* Fiyat Bilgileri */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">
                                <FaCalculator className="me-2" />
                                Fiyat Bilgileri
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-muted">Alış Fiyatı</Form.Label>
                                <div>
                                    {displayValue(vehicle.purchasePrice, 'Alış fiyatı yok', (val) => `${formatNumber(val)} ₺`)}
                                </div>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-muted">Satış Fiyatı</Form.Label>
                                <div>
                                    {displayValue(vehicle.salePrice, 'Satış fiyatı yok', (val) => `${formatNumber(val)} ₺`)}
                                </div>
                            </Form.Group>

                            {analytics && (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-muted">Toplam Masraf</Form.Label>
                                        <div>
                                            {formatNumber(analytics.totalExpenses)} ₺
                                        </div>
                                    </Form.Group>

                                    {analytics.profitLoss !== null && (
                                        <Form.Group className="mb-3">
                                            <Form.Label className="text-muted">
                                                {analytics.profitLoss >= 0 ? 'Kar' : 'Zarar'}
                                            </Form.Label>
                                            <div>
                                                {formatNumber(analytics.profitLoss)} ₺
                                            </div>
                                        </Form.Group>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>

                    {/* İşlemler */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">İşlemler</h5>
                        </Card.Header>
                        <Card.Body className="d-grid gap-2">
                            <Button
                                as={Link}
                                to={`/vehicles/edit/${vehicle._id}`}
                                variant="primary"
                            >
                                <FaEdit className="me-2" />
                                Düzenle
                            </Button>

                            {canEdit && (
                                <>
                                    {vehicle.status === 'active' && (
                                        <>
                                            <Button
                                                variant="warning"
                                                onClick={() => handleStatusChange('reserved')}
                                            >
                                                Rezerve Et
                                            </Button>
                                            <Button
                                                variant="success"
                                                onClick={() => setShowSaleModal(true)}
                                            >
                                                Sat
                                            </Button>
                                        </>
                                    )}

                                    {vehicle.status === 'reserved' && (
                                        <>
                                            <Button
                                                variant="primary"
                                                onClick={() => handleStatusChange('active')}
                                            >
                                                Aktifleştir
                                            </Button>
                                            <Button
                                                variant="success"
                                                onClick={() => setShowSaleModal(true)}
                                            >
                                                Sat
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modals */}
            <Modal show={showExpenseModal} onHide={() => setShowExpenseModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Masraf Ekle</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddExpense}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Masraf Tipi</Form.Label>
                            <Form.Select
                                value={expenseForm.type}
                                onChange={(e) => setExpenseForm({ ...expenseForm, type: e.target.value })}
                                required
                            >
                                {Object.entries(masrafTypes).map(([key, value]) => (
                                    <option key={key} value={key}>{value}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Tutar (₺)</Form.Label>
                            <Form.Control
                                type="number"
                                value={expenseForm.amount}
                                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Açıklama</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={expenseForm.description}
                                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Tarih</Form.Label>
                            <Form.Control
                                type="date"
                                value={expenseForm.date}
                                onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowExpenseModal(false)}>
                            İptal
                        </Button>
                        <Button variant="primary" type="submit">
                            Ekle
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showSaleModal} onHide={() => setShowSaleModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Araç Satışı</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Müşteri Tipi</Form.Label>
                        <Form.Select
                            value={saleForm.type}
                            onChange={(e) => setSaleForm({ ...saleForm, type: e.target.value })}
                        >
                            <option value="individual">Şahıs</option>
                            <option value="dealer">Bayii</option>
                            <option value="gallery">Galeri</option>
                            <option value="export">İhracat</option>
                            <option value="other">Diğer</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Müşteri Adı</Form.Label>
                        <Form.Control
                            type="text"
                            value={saleForm.name}
                            onChange={(e) => setSaleForm({ ...saleForm, name: e.target.value })}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>İletişim</Form.Label>
                        <Form.Control
                            type="text"
                            value={saleForm.contact}
                            onChange={(e) => setSaleForm({ ...saleForm, contact: e.target.value })}
                            placeholder="Telefon veya e-posta"
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Satış Tarihi</Form.Label>
                        <Form.Control
                            type="date"
                            value={saleForm.date}
                            onChange={(e) => setSaleForm({ ...saleForm, date: e.target.value })}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSaleModal(false)}>
                        İptal
                    </Button>
                    <Button variant="success" onClick={() => handleStatusChange('sold')}>
                        Sat
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default VehicleDetailPage;